/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 * 
 * Theme Hooks - Custom React hooks for accessing theme utilities
 */

import { useContext } from 'react';
import { ThemeContext, ThemeContextType, ThemeMode, ThemePreference } from './ThemeContext';
import theme from './index';

/**
 * Hook to access the current theme mode and toggle functions
 * @returns {ThemeContextType} Theme context with mode and toggle functions
 * @throws {Error} If used outside of ThemeProvider
 */
export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};


/**
 * Hook to access theme typography settings
 */
export const useThemeTypography = () => {
    return theme.typography;
};

/**
 * Hook to access theme spacing utilities
 */
export const useSpacing = () => {
    return theme.spacing;
};

/**
 * Hook to access theme breakpoints
 */
export const useThemeBreakpoints = () => {
    return theme.breakpoints;
};

/**
 * Hook to access theme shadows
 */
export const useThemeShadows = () => {
    return theme.shadows;
};

/**
 * Hook to get all theme variables
 */
export const useThemeConfig = () => {
    return theme;
};

/**
 * Hook to check current theme mode
 */
export const useIsDarkMode = (): boolean => {
    const { mode } = useTheme();
    return mode === 'dark';
};

/**
 * Hook to check current theme mode
 */
export const useIsLightMode = (): boolean => {
    const { mode } = useTheme();
    return mode === 'light';
};

/**
 * Hook to toggle theme mode
 */
export const useToggleTheme = (): (() => void) => {
    const { toggleTheme } = useTheme();
    return toggleTheme;
};

/**
 * Hook to set theme mode explicitly
 */
export const useSetTheme = (): ((mode: ThemeMode) => void) => {
    const { setTheme } = useTheme();
    return setTheme;
};

/**
 * Hook to get/set the theme preference (light | dark | system)
 */
export const useThemePreference = (): { preference: ThemePreference; setPreference: (pref: ThemePreference) => void } => {
    const { preference, setPreference } = useTheme();
    return { preference, setPreference };
};

const themeHooks = {
    useTheme,
    useThemeTypography,
    useSpacing,
    useThemeBreakpoints,
    useThemeShadows,
    useThemeConfig,
    useIsDarkMode,
    useIsLightMode,
    useToggleTheme,
    useSetTheme,
    useThemePreference,
};

export default themeHooks;
